import React, { useContext, useEffect } from 'react'
import { ToastContext, useIsTouchScreen, useToast } from '@afterpaytouch/core'
import { revokePersistentSession, useAppDispatch } from '../../state'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { useRoutePageName } from '../../config/router'
import { consumeRememberMeMinted, subscribeRememberMeMinted } from '../../flow/remember-me/issuanceSignal'
import { setOptedOutRememberMe } from '../../flow/remember-me/optOut'
import { ToastMessage } from './ToastMessage'

const TOAST_NAMESPACE = 'auto-remember-me-issuance'

/**
 * CASHDAP-3292: shows the first-time remember-me issuance toast once, on the first summary render
 * after a remember-me session was minted this visit (the one-shot signal set by
 * useMintPersistentSessionOnLogin — so this only ever fires when the mint's flag gate passed and
 * the mint actually succeeded; no separate flag read needed here).
 *
 * "Got it" acknowledges and dismisses. "Not on this device" persists the per-device opt-out (the
 * mint hook never mints on this device again), revokes the just-minted cookie server-side, and
 * dismisses.
 */
export const useAutoRememberMeIssuanceToast = (): void => {
  const toast = useToast()
  const { setToastList } = useContext(ToastContext)
  const isTouchScreen = useIsTouchScreen()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const pageName = useRoutePageName()
  const dispatch = useAppDispatch()

  useEffect(() => {
    const showToast = (): void => {
      // Core Toast has no public per-toast dismiss handle, so remove ours from the provider's list
      // by its testNameSpace. This skips the exit transition, which is fine for an explicit tap,
      // and leaves any other toast (e.g. the payment-schedule toast) untouched.
      const dismiss = (): void => {
        setToastList((list) => list.filter((item) => item.testNameSpace !== TOAST_NAMESPACE))
      }

      const onGotIt = (): void => {
        logEvent(TrackingEvent.PRESSED_AUTO_REMEMBER_ME_ISSUANCE_TOAST_GOT_IT)
        dismiss()
      }

      const onOptOut = (): void => {
        logEvent(TrackingEvent.PRESSED_AUTO_REMEMBER_ME_ISSUANCE_TOAST_OPT_OUT)
        setOptedOutRememberMe()
        // CASHDAP-3258: revoke the just-minted cookie server-side (cookie-only — the checkout
        // session is kept). Fire-and-forget: the toast dismisses immediately, and the device-side
        // opt-out (localStorage + mint/resume gates) already holds if this call fails.
        void dispatch(revokePersistentSession()).then((result) => {
          // CASHDAP-3556: the thunk resolves false when nothing was revoked, so only a fulfilled
          // true means a remember-me session actually ended server-side.
          if (revokePersistentSession.fulfilled.match(result) && result.payload === true) {
            logEvent(TrackingEvent.REMEMBER_ME_REVOKED, { revokeReason: 'userRevoked', scope: 'this-device', pageName })
          }
        })
        dismiss()
      }

      logEvent(TrackingEvent.VIEWED_AUTO_REMEMBER_ME_ISSUANCE_TOAST, { pageName })
      toast({
        message: () => <ToastMessage onGotIt={onGotIt} onOptOut={onOptOut} />,
        kind: 'Default',
        toastTime: 5000,
        swipeToDismiss: isTouchScreen,
        testNameSpace: TOAST_NAMESPACE,
      })
    }

    // Consume-then-subscribe: the best-effort mint races the post-login routing, so the signal may
    // be marked either before this hook mounts (consumed here) or after (delivered via the
    // subscription). Consuming inside the callback keeps the one-shot contract in both orders.
    const maybeShow = (): void => {
      if (consumeRememberMeMinted()) {
        showToast()
      }
    }
    maybeShow()
    return subscribeRememberMeMinted(maybeShow)
  }, [toast, setToastList, isTouchScreen, logEvent, pageName, dispatch])
}
